<script setup>
import { FunnelIcon, XCircleIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import MonthAndYearField from "@js/components/fields/MonthAndYearField.vue";
import moment from "moment";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { filterDefinitions, filters, setFilters } = defineProps({
  filterDefinitions: {
    type: Array,
    required: true,
  },
  filters: {
    type: Array,
    required: true,
  },
  setFilters: {
    type: Function,
    required: true,
  },
});

const currentFilters = ref(initCurrentFilters());

const filtersDrawerIsOpen = ref(false);

function setFiltersDrawerIsOpen(value) {
  filtersDrawerIsOpen.value = value;
}

function initCurrentFilters() {
  let tmpCurrentFilters = {};

  for (const filterDefinition of Object.values(filterDefinitions)) {
    let filterIndex = filters.findIndex(
      (filter) => filter.id === filterDefinition.id,
    );

    if (filterIndex === -1) {
      if (
        filterDefinition["type"] === "range" ||
        filterDefinition["type"] === "dateRange"
      ) {
        tmpCurrentFilters[filterDefinition.id] = [null, null];
      } else {
        tmpCurrentFilters[filterDefinition.id] = null;
      }
    } else {
      if (
        filterDefinition["type"] === "range" ||
        filterDefinition["type"] === "dateRange"
      ) {
        tmpCurrentFilters[filterDefinition.id] = [
          filters[filterIndex].value[0],
          filters[filterIndex].value[1],
        ];
      } else if (filterDefinition["type"] === "monthAndYear") {
        tmpCurrentFilters[filterDefinition.id] = {
          month: filters[filterIndex].value.month,
          year: filters[filterIndex].value.year,
        };
      } else if (
        filterDefinition["type"] !== "listbox" &&
        filterDefinition["type"] !== "combobox"
      ) {
        tmpCurrentFilters[filterDefinition.id] = filters[filterIndex].value;
      } else {
        let label;

        if (filterDefinition.options.length > 0) {
          let search = filterDefinition.options.find(
            (option) => option.value === filters[filterIndex].value,
          );

          label = search ? search.label : filters[filterIndex].value;
        } else {
          label = filters[filterIndex].value;
        }

        tmpCurrentFilters[filterDefinition.id] = {
          value: filters[filterIndex].value,
          label: label,
        };
      }
    }
  }

  return tmpCurrentFilters;
}

function applyFilters() {
  setFiltersDrawerIsOpen(false);
  let tmpFilters = filters;

  for (let filterDefinition of Object.values(filterDefinitions)) {
    let currentFilterOption = currentFilters.value[filterDefinition.id];
    let hasFilter =
      filterDefinition.type === "range" || filterDefinition.type === "dateRange"
        ? currentFilterOption[0] !== null || currentFilterOption[1] !== null
        : currentFilterOption !== null && currentFilterOption !== undefined;
    let filterIndex = tmpFilters.findIndex(
      (filter) => filter.id === filterDefinition.id,
    );

    if (hasFilter) {
      if (filterIndex === -1) {
        let value;

        if (Array.isArray(currentFilterOption)) {
          value = currentFilterOption;
        } else if (typeof currentFilterOption === "object") {
          if (filterDefinition.formattedValue) {
            value = filterDefinition.formattedValue(currentFilterOption.value);
          } else if (typeof currentFilterOption.value === "undefined") {
            value = currentFilterOption;
          } else {
            value = currentFilterOption.value;
          }
        } else {
          if (filterDefinition.formattedValue) {
            value = filterDefinition.formattedValue(currentFilterOption);
          } else {
            value = currentFilterOption;
          }
        }

        tmpFilters.push({
          id: filterDefinition.id,
          column: filterDefinition.column,
          label: filterDefinition.label,
          operator: filterDefinition.operator,
          value: value,
          valueLabel:
            typeof currentFilterOption === "object"
              ? currentFilterOption.label
              : currentFilterOption,
        });
      } else {
        if (Array.isArray(currentFilterOption)) {
          tmpFilters[filterIndex].value = currentFilterOption;
        } else {
          tmpFilters[filterIndex].value =
            typeof currentFilterOption === "object" &&
            typeof currentFilterOption.value !== "undefined"
              ? currentFilterOption.value
              : currentFilterOption;
          tmpFilters[filterIndex].valueLabel =
            typeof currentFilterOption === "object" &&
            typeof currentFilterOption.label !== "undefined"
              ? currentFilterOption.label
              : currentFilterOption;
        }
      }
    } else {
      if (filterIndex !== -1) {
        tmpFilters.splice(filterIndex, 1);
      }
    }
  }

  setFilters(tmpFilters);
}

function resetFilters() {
  currentFilters.value = {};
  applyFilters();
}

function removeFilter(filterId) {
  let tmpFilters = filters;
  let filterIndex = tmpFilters.findIndex((filter) => filter.id === filterId);

  if (filterIndex !== -1) {
    tmpFilters.splice(filterIndex, 1);
  }

  setFilters(tmpFilters);
}

function filterIsQuick(filterId) {
  return filterDefinitions.find(
    (filterDefinition) => filterDefinition.id === filterId,
  ).quick;
}

function getFormattedFilter(filter) {
  let formattedFilter = filter.label + " : ";

  if (filter.valueLabel) {
    formattedFilter += filter.valueLabel;
  } else {
    if (Array.isArray(filter.value)) {
      for (let i = 0; i < filter.value.length; i++) {
        let date = moment(filter.value[i], "YYYY-MM-DD", true);

        if (filter.value[i] === null) {
          formattedFilter += "N/A";
        } else if (moment(filter.value[i], "YYYY-MM-DD", true).isValid()) {
          formattedFilter += date.format("DD/MM/YYYY");
        } else {
          formattedFilter += filter.value[i];
        }

        if (i < filter.value.length - 1) {
          formattedFilter += " - ";
        }
      }
    } else {
      formattedFilter += filter.value;
    }
  }

  return formattedFilter;
}
</script>

<template>
  <div
    class="flex flex-row-reverse justify-end gap-2 lg:flex-row lg:justify-start"
  >
    <div v-if="filters.length > 0" class="flex flex-wrap gap-2">
      <div
        v-for="(filter, index) in filters"
        :key="index"
        class="flex h-[28px] cursor-pointer items-center gap-2 rounded border border-indigo-600 px-3 py-1 text-indigo-600 hover:bg-indigo-600 hover:text-white"
        :class="{ hidden: filterIsQuick(filter.id) }"
      >
        {{ getFormattedFilter(filter) }}
        <button type="button" @click="removeFilter(filter.id)">
          <XCircleIcon class="size-4" />
        </button>
      </div>
    </div>

    <button
      type="button"
      class="flex items-center gap-2 rounded bg-gray-200 px-3 py-1 text-sm text-indigo-600 duration-150 hover:bg-indigo-600 hover:text-white"
      @click="setFiltersDrawerIsOpen(true)"
    >
      <FunnelIcon class="size-4" />
      {{ t("all.button.filters") }}
    </button>
  </div>

  <Drawer :set-is-open="setFiltersDrawerIsOpen" :is-open="filtersDrawerIsOpen">
    <template #title>
      {{ t("all.title.filters") }}
    </template>

    <template #content>
      <template
        v-for="(filterDefinition, index) in filterDefinitions"
        :key="index"
      >
        <ListboxField
          v-if="filterDefinition.type === 'listbox'"
          v-model="currentFilters[filterDefinition.id]"
          :options="filterDefinition.options"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
          has-empty-option
          by="value"
        >
          <template #buttonContent>
            {{
              currentFilters[filterDefinition.id]
                ? currentFilters[filterDefinition.id].label
                : "&nbsp;"
            }}
          </template>

          <template #optionContent="{ option }">
            {{ option.label }}
          </template>
        </ListboxField>

        <ComboboxField
          v-else-if="filterDefinition.type === 'combobox'"
          v-model="currentFilters[filterDefinition.id]"
          :options="filterDefinition.options"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
          filtered-option-key="label"
          has-empty-option
          by="value"
        >
          <template #optionContent="{ option }">
            {{ option.label }}
          </template>
        </ComboboxField>

        <InputField
          v-else-if="filterDefinition.type === 'text'"
          v-model="currentFilters[filterDefinition.id]"
          :options="filterDefinition.options"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
        />

        <InputField
          v-else-if="filterDefinition.type === 'number'"
          v-model="currentFilters[filterDefinition.id]"
          :options="filterDefinition.options"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
          type="number"
        />

        <InputField
          v-else-if="filterDefinition.type === 'date'"
          v-model="currentFilters[filterDefinition.id]"
          :options="filterDefinition.options"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
          type="date"
        />

        <div
          v-else-if="filterDefinition.type === 'dateRange'"
          class="flex flex-col gap-2"
        >
          <label :for="filterDefinition.id">
            {{ filterDefinition.label }}
          </label>

          <div class="flex gap-2">
            <InputField
              v-model="currentFilters[filterDefinition.id][0]"
              :options="filterDefinition.options"
              :field-name="filterDefinition.id"
              :field-id="filterDefinition.id"
              type="date"
              class="w-full"
            />

            <InputField
              v-model="currentFilters[filterDefinition.id][1]"
              :options="filterDefinition.options"
              :field-name="filterDefinition.id"
              :field-id="filterDefinition.id"
              type="date"
              class="w-full"
            />
          </div>
        </div>

        <div
          v-else-if="filterDefinition.type === 'range'"
          class="flex flex-col gap-2"
        >
          <label :for="filterDefinition.id">
            {{ filterDefinition.label }}
          </label>

          <div class="flex gap-2">
            <InputField
              v-model="currentFilters[filterDefinition.id][0]"
              :options="filterDefinition.options"
              :field-name="`${filterDefinition.id}-from`"
              :field-id="`${filterDefinition.id}-from`"
              type="number"
            />

            <InputField
              v-model="currentFilters[filterDefinition.id][1]"
              :options="filterDefinition.options"
              :field-name="`${filterDefinition.id}-to`"
              :field-id="`${filterDefinition.id}-to`"
              type="number"
            />
          </div>
        </div>

        <MonthAndYearField
          v-else-if="filterDefinition.type === 'monthAndYear'"
          v-model="currentFilters[filterDefinition.id]"
          :label="filterDefinition.label"
          :field-name="filterDefinition.id"
          :field-id="filterDefinition.id"
          :has-empty-option="
            filterDefinition.hasEmptyOption === undefined ||
            filterDefinition.hasEmptyOption === true
          "
        />
      </template>
    </template>

    <template #footer>
      <div class="flex gap-2">
        <ActionButton
          type="secondary"
          :callback="() => resetFilters()"
          class="flex-1"
        >
          {{ t("all.button.reset") }}
        </ActionButton>

        <ActionButton :callback="() => applyFilters()" class="flex-1">
          {{ t("all.button.apply") }}
        </ActionButton>
      </div>
    </template>
  </Drawer>
</template>
